/**
 * ============================================================
 *  ASSISTENTE FINANCEIRO — chat Text-to-SQL sobre o banco do cliente
 * ============================================================
 *  Uso:
 *    <AssistenteFinanceiro clienteId={id} />              → bolha flutuante
 *    <AssistenteFinanceiro clienteId={id} modo="pagina" /> → tela cheia
 *
 *  Chama POST /api/cliente-bpo/:id/assistente/ { pergunta }.
 *  Paleta FMP: #00205b (navy primário) · #00b5cc (ciano acento) · #008fa1 (ciano escuro p/ fundos com texto branco).
 * ============================================================
 */
import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_BASE;

const NAVY = '#00205b';        // navy FMP (primário)
const CYAN = '#00b5cc';        // ciano FMP (acento — só sobre fundos claros)
const CYAN_ESCURO = '#008fa1'; // ciano escuro (fundos que recebem texto/ícone branco)

const SUGESTOES = [
  { icon: 'fa-triangle-exclamation', texto: 'Quantas contas estão vencidas?' },
  { icon: 'fa-hand-holding-dollar',  texto: 'Quanto eu tenho a receber ainda?' },
  { icon: 'fa-chart-line',           texto: 'Como está meu faturamento este mês?' },
  { icon: 'fa-money-bill-trend-up',  texto: 'Quais minhas maiores despesas do mês?' },
  { icon: 'fa-calendar-day',         texto: 'Quais contas vencem hoje?' },
  { icon: 'fa-scale-balanced',       texto: 'Qual meu saldo entre receber e pagar?' },
];

function renderMarkdown(texto) {
  return texto.split('\n').map((linha, i) => {
    const isBullet = linha.trimStart().startsWith('- ') || linha.trimStart().startsWith('• ');
    if (isBullet) {
      const conteudo = linha.replace(/^\s*[-•]\s*/, '');
      const cf = conteudo.split(/\*\*(.*?)\*\*/g).map((p, j) =>
        j % 2 === 1 ? <strong key={j} style={{ color: NAVY }}>{p}</strong> : p);
      return (
        <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 3 }}>
          <span style={{ color: CYAN, flexShrink: 0, marginTop: 1 }}>•</span>
          <span>{cf}</span>
        </div>
      );
    }
    if (!linha.trim()) return <div key={i} style={{ height: 6 }} />;
    const partes = linha.split(/\*\*(.*?)\*\*/g).map((p, j) =>
      j % 2 === 1 ? <strong key={j} style={{ color: NAVY }}>{p}</strong> : p);
    return <div key={i} style={{ marginBottom: 2 }}>{partes}</div>;
  });
}

/* ── Corpo do chat (compartilhado entre bolha e página) ── */
function ChatCore({ clienteId, compacto }) {
  const [mensagens, setMensagens] = useState([]);
  const [pergunta, setPergunta]   = useState('');
  const [carregando, setCarregando] = useState(false);
  const fimRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => { fimRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [mensagens, carregando]);

  const enviar = async (textoDireto) => {
    const texto = (textoDireto ?? pergunta).trim();
    if (!texto || carregando) return;
    const token = localStorage.getItem('access');
    const novaId = Date.now();

    // histórico recente (antes de adicionar a nova pergunta) p/ resolver referências
    const historico = mensagens.slice(-6).map(m => ({ role: m.role, content: m.content }));

    setMensagens(prev => [...prev, { id: novaId, role: 'user', content: texto, ts: new Date() }]);
    setPergunta('');
    setCarregando(true);
    try {
      const res = await axios.post(
        `${API}/api/cliente-bpo/${clienteId}/assistente/`,
        { pergunta: texto, historico },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMensagens(prev => [...prev, {
        id: novaId + 1, role: 'assistant', content: res.data.resposta,
        ts: new Date(), tipo: res.data.tipo_consulta,
      }]);
    } catch (err) {
      const detalhe = err.response?.data?.erro || err.message || 'Erro desconhecido';
      setMensagens(prev => [...prev, {
        id: novaId + 1, role: 'assistant',
        content: `Não consegui processar sua pergunta. ${detalhe}`, ts: new Date(), tipo: 'erro',
      }]);
    } finally {
      setCarregando(false);
      inputRef.current?.focus();
    }
  };

  const fmtHora = (d) => d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  const temConversa = mensagens.length > 0 || carregando;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, background: '#f2f5fb' }}>
      {/* mensagens */}
      <div style={{ flex: 1, overflowY: 'auto', padding: compacto ? '1rem' : '1.5rem' }}>
        {!temConversa && (
          <div style={{ textAlign: 'center', padding: compacto ? '1rem 0.5rem' : '2rem 1rem', maxWidth: 780, margin: '0 auto' }}>
            <div style={{
              width: compacto ? 56 : 84, height: compacto ? 56 : 84, borderRadius: '50%',
              background: `linear-gradient(135deg, ${NAVY}, ${CYAN_ESCURO})`, margin: '0 auto 1rem',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 8px 26px rgba(0,32,91,0.28)',
            }}>
              <i className="fa-solid fa-rocket" style={{ color: '#fff', fontSize: compacto ? '1.4rem' : '2.2rem' }} />
            </div>
            <h5 style={{ fontWeight: 800, color: NAVY, marginBottom: 6 }}>Assistente Financeiro FMP</h5>
            <p style={{ color: '#64748b', fontSize: compacto ? '0.85rem' : '0.98rem', marginBottom: '1.5rem' }}>
              Pergunte sobre contas, faturamento, recebíveis e despesas do seu financeiro.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: compacto ? '1fr' : '1fr 1fr', gap: 10 }}>
              {(compacto ? SUGESTOES.slice(0, 4) : SUGESTOES).map((s, i) => (
                <button key={i} onClick={() => enviar(s.texto)}
                  style={{
                    background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 14,
                    padding: '0.85rem 1rem', fontSize: '0.87rem', color: '#334155',
                    textAlign: 'left', cursor: 'pointer', display: 'flex', gap: 10, alignItems: 'center',
                    transition: 'border-color .2s, box-shadow .2s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = CYAN; e.currentTarget.style.boxShadow = '0 4px 14px rgba(0,181,204,0.25)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.boxShadow = 'none'; }}>
                  <i className={`fas ${s.icon}`} style={{ color: CYAN, flexShrink: 0 }} />
                  <span>{s.texto}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {mensagens.map((msg) => (
          <div key={msg.id} className={`d-flex mb-3 ${msg.role === 'user' ? 'justify-content-end' : 'justify-content-start'}`}
               style={{ maxWidth: 860, margin: '0 auto 0.75rem' }}>
            {msg.role === 'assistant' && (
              <div style={{
                width: 32, height: 32, borderRadius: '50%', flexShrink: 0, marginRight: 10, alignSelf: 'flex-end',
                background: `linear-gradient(135deg, ${NAVY}, ${CYAN_ESCURO})`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <i className="fa-solid fa-rocket" style={{ color: '#fff', fontSize: '0.75rem' }} />
              </div>
            )}
            <div style={{ maxWidth: '80%' }}>
              <div style={{
                padding: '0.75rem 1rem',
                borderRadius: msg.role === 'user' ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                background: msg.role === 'user' ? NAVY : (msg.tipo === 'erro' ? '#fff3cd' : '#fff'),
                color: msg.role === 'user' ? '#fff' : '#1e293b',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)', fontSize: '0.92rem', lineHeight: 1.6,
              }}>
                {msg.role === 'user' ? msg.content : renderMarkdown(msg.content)}
              </div>
              <div className="d-flex align-items-center gap-2 mt-1"
                   style={{ justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
                <small className="text-muted" style={{ fontSize: '0.66rem' }}>{fmtHora(msg.ts)}</small>
              </div>
            </div>
          </div>
        ))}

        {carregando && (
          <div style={{ maxWidth: 860, margin: '0 auto 1rem', display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{
              width: 28, height: 28, borderRadius: '50%', background: `linear-gradient(135deg, ${NAVY}, ${CYAN_ESCURO})`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <i className="fa-solid fa-rocket" style={{ color: '#fff', fontSize: '0.7rem' }} />
            </div>
            <div style={{ display: 'flex', gap: 4 }}>
              {[0, 1, 2].map(i => (
                <div key={i} style={{ width: 7, height: 7, borderRadius: '50%', background: CYAN,
                  animation: `afTyping 1.2s ease-in-out ${i * 0.2}s infinite` }} />
              ))}
            </div>
          </div>
        )}
        <div ref={fimRef} />
      </div>

      {/* input */}
      <div style={{ flexShrink: 0, padding: compacto ? '0.75rem' : '1rem 1.5rem 1.25rem', background: '#f2f5fb' }}>
        <div style={{
          maxWidth: 860, margin: '0 auto', background: '#fff', border: '1.5px solid #e2e8f0',
          borderRadius: 16, boxShadow: '0 4px 16px rgba(0,0,0,0.06)', padding: '0.5rem 0.5rem 0.5rem 1rem',
          display: 'flex', alignItems: 'flex-end', gap: 8,
        }}>
          <textarea
            ref={inputRef}
            placeholder="Faça uma pergunta sobre seu financeiro…"
            value={pergunta}
            onChange={e => setPergunta(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); enviar(); } }}
            rows={1}
            disabled={carregando}
            style={{
              flex: 1, border: 'none', outline: 'none', resize: 'none', background: 'transparent',
              fontSize: '0.92rem', lineHeight: 1.5, maxHeight: 120, padding: '0.4rem 0', color: '#0f172a',
            }}
          />
          <button onClick={() => enviar()} disabled={!pergunta.trim() || carregando}
            style={{
              width: 40, height: 40, borderRadius: '50%', border: 'none', flexShrink: 0,
              background: pergunta.trim() && !carregando ? `linear-gradient(135deg, ${NAVY}, ${CYAN_ESCURO})` : '#e2e8f0',
              color: pergunta.trim() && !carregando ? '#fff' : '#94a3b8',
              cursor: pergunta.trim() && !carregando ? 'pointer' : 'default',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
            <i className={`fas ${carregando ? 'fa-spinner fa-spin' : 'fa-paper-plane'}`} />
          </button>
        </div>
      </div>

      <style>{`
        @keyframes afTyping { 0%,60%,100% { transform: translateY(0); opacity: .4; } 30% { transform: translateY(-7px); opacity: 1; } }
      `}</style>
    </div>
  );
}

export default function AssistenteFinanceiro({ clienteId, modo = 'bolha' }) {
  const [aberto, setAberto] = useState(false);
  if (!clienteId) return null;

  /* ── Página cheia ── */
  if (modo === 'pagina') {
    return (
      <div style={{ position: 'fixed', top: 0, left: '6rem', right: 0, bottom: 0, zIndex: 1 }}>
        <ChatCore clienteId={clienteId} compacto={false} />
      </div>
    );
  }

  /* ── Bolha flutuante ── */
  return (
    <>
      {aberto && (
        <div style={{
          position: 'fixed', bottom: 96, right: 24, zIndex: 1050,
          width: 'min(400px, calc(100vw - 32px))', height: 'min(600px, calc(100vh - 130px))',
          background: '#fff', borderRadius: 18, overflow: 'hidden',
          boxShadow: '0 16px 48px rgba(0,32,91,0.28)', display: 'flex', flexDirection: 'column',
          border: '1px solid #e2e8f0',
        }}>
          <div style={{
            flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '0.85rem 1rem', background: `linear-gradient(135deg, ${NAVY}, #003580)`, color: '#fff',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <i className="fa-solid fa-rocket" style={{ color: CYAN }} />
              <div style={{ lineHeight: 1.1 }}>
                <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>Assistente Financeiro FMP</div>
                <div style={{ fontSize: '0.66rem', opacity: 0.75 }}>Consulta em tempo real</div>
              </div>
            </div>
            <button onClick={() => setAberto(false)}
              style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: '1.1rem', cursor: 'pointer' }}>
              <i className="fas fa-times" />
            </button>
          </div>
          <div style={{ flex: 1, minHeight: 0 }}>
            <ChatCore clienteId={clienteId} compacto />
          </div>
        </div>
      )}

      <button onClick={() => setAberto(a => !a)} title="Assistente Financeiro FMP"
        style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 1050,
          width: 60, height: 60, borderRadius: '50%', border: 'none', cursor: 'pointer',
          background: `linear-gradient(135deg, ${NAVY}, ${CYAN_ESCURO})`, color: '#fff',
          boxShadow: '0 8px 26px rgba(0,32,91,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'transform .15s',
        }}
        onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.07)'; }}
        onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}>
        <i className={`fa-solid ${aberto ? 'fa-times' : 'fa-rocket'}`} style={{ fontSize: '1.4rem' }} />
      </button>
    </>
  );
}
